import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ===== Per-Section Walkthrough Banner =====
// Dismissible but stays until "Got it" is tapped. Re-shows on next login until dismissed.
export const WalkthroughBanner = ({ sectionKey, title, body, icon = "💡", color = "#00FFFF" }) => {
  const storageKey = `mf_owner_walkthrough_${sectionKey}`;
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(storageKey));
  const [expanded, setExpanded] = useState(true);

  if (dismissed) return null;

  return (
    <div data-testid={`owner-walkthrough-${sectionKey}`} style={{
      background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
      border: `1.5px solid ${color}40`,
      borderRadius: 12,
      padding: "14px 16px",
      marginBottom: 18,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }} onClick={() => setExpanded(e => !e)}>
            <span style={{ fontSize: 18 }}>{icon}</span>
            <strong style={{ color: "#fff", fontSize: 14 }}>{title}</strong>
            <span style={{ fontSize: 11, color: color, marginLeft: "auto" }}>{expanded ? "▼" : "▶"}</span>
          </div>
          {expanded && (
            <div style={{ color: "#ADBED1", fontSize: 13, lineHeight: 1.55, paddingLeft: 26 }}>
              {body}
            </div>
          )}
        </div>
      </div>
      {expanded && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10, paddingLeft: 26 }}>
          <button
            onClick={() => { localStorage.setItem(storageKey, "1"); setDismissed(true); }}
            data-testid={`owner-walkthrough-${sectionKey}-dismiss`}
            style={{ padding: "6px 14px", borderRadius: 6, border: `1px solid ${color}`, background: `${color}22`, color: color, fontSize: 12, fontWeight: 800, cursor: "pointer" }}
          >
            ✓ Got it
          </button>
        </div>
      )}
    </div>
  );
};

// ===== First-Login Welcome Tour =====
export const FirstLoginTour = ({ userName = "" }) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(() => !localStorage.getItem("mf_owner_tour_seen"));

  if (!visible) return null;

  const steps = [
    {
      title: `Welcome${userName ? `, ${userName.split(" ")[0]}` : ""} 👋`,
      body: (
        <>
          <p style={{ margin: "0 0 10px" }}>This is your <strong>Client Portal</strong> for Moovin Forward. Everything happening with your property — every photo, document, message, and invoice — lives here.</p>
          <p style={{ margin: 0 }}>Quick 60-second tour?</p>
        </>
      ),
    },
    {
      title: "🏠 Projects",
      body: <p style={{ margin: 0 }}>Each property you have with us is a "project." Tap one to see its full timeline: scope, status, photos, materials, invoices. Updates happen here in real-time as our crew works.</p>,
    },
    {
      title: "📸 Photos & Videos",
      body: <p style={{ margin: 0 }}>Every photo our crew takes is <strong>timestamped, dated, and watermarked with the property address</strong>. It's your unimpeachable proof of work. Filter by <em>before</em>, <em>after</em>, <em>progress</em>, or <em>issue</em>.</p>,
    },
    {
      title: "🔴 Field Triggers",
      body: <p style={{ margin: 0 }}>If our crew finds an issue mid-job, you'll get an email with a photo and a one-tap link to view + reply. You don't need to log in — just respond from your phone. <strong>Total transparency, zero surprises.</strong></p>,
    },
    {
      title: "📄 Documents & Packages",
      body: <p style={{ margin: 0 }}>Receivership packets, contracts, pitch packages — every PDF we send is saved here. Each package has a short <em>"What's this?"</em> explainer so you always know what you're looking at and what (if anything) you need to do.</p>,
    },
    {
      title: "💳 Invoices",
      body: <p style={{ margin: 0 }}>See what's been paid, what's pending, and what's coming. Pay any invoice with one tap via Stripe. All payment history stays in your portal forever.</p>,
    },
    {
      title: "💬 Reminders Stay With You",
      body: <p style={{ margin: 0 }}>Every section has a "What is this?" reminder card. Even after you dismiss them, you can re-open the cards anytime by tapping the small <strong>💡 Guide</strong> link at the top of each section. Take your time — we built this for clarity.</p>,
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  const close = () => {
    localStorage.setItem("mf_owner_tour_seen", "1");
    setVisible(false);
  };

  return (
    <div data-testid="owner-first-login-tour" style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.95)", backdropFilter: "blur(14px)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
      <div style={{ background: "linear-gradient(135deg, #0a1929 0%, #030B1A 100%)", border: "1.5px solid rgba(0,255,255,0.30)", borderRadius: 14, padding: 26, maxWidth: 500, width: "100%" }}>
        <div style={{ fontSize: 10, color: "#00FFFF", letterSpacing: "0.14em", fontWeight: 800, marginBottom: 6 }}>MOOVIN FORWARD · {step + 1} of {steps.length}</div>
        <h2 style={{ color: "#fff", margin: "0 0 14px", fontSize: 22 }}>{current.title}</h2>
        <div style={{ color: "#ADBED1", fontSize: 14, lineHeight: 1.6, marginBottom: 22 }}>
          {current.body}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button onClick={close} data-testid="owner-tour-skip" style={{ padding: "8px 14px", background: "transparent", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 6, color: "#7E92A8", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Skip tour
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} data-testid="owner-tour-back" style={{ padding: "8px 14px", border: "1px solid rgba(255,255,255,0.15)", background: "transparent", color: "#ADBED1", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                ← Back
              </button>
            )}
            <button onClick={() => isLast ? close() : setStep(s => s + 1)} data-testid="owner-tour-next" style={{ padding: "8px 18px", border: "1px solid #00DC78", background: "rgba(0,220,120,0.18)", color: "#00DC78", borderRadius: 6, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
              {isLast ? "Got it ✓" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== Unread Triggers Red Badge (for Owner) =====
export const UnreadTriggerBadge = () => {
  const [unread, setUnread] = useState(0);
  const [triggers, setTriggers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/portal/triggers/unread-count`, { credentials: "include" });
        if (r.ok) {
          const data = await r.json();
          setUnread(data.unread || 0);
          setTriggers(data.active_triggers || []);
        }
      } catch {}
    };
    load();
    const interval = setInterval(load, 90000);
    return () => clearInterval(interval);
  }, []);

  if (unread === 0) return null;

  return (
    <div data-testid="owner-unread-trigger-badge" style={{
      background: "linear-gradient(135deg, rgba(255,77,109,0.15) 0%, rgba(255,77,109,0.05) 100%)",
      border: "1.5px solid rgba(255,77,109,0.50)",
      borderRadius: 12,
      padding: "12px 16px",
      marginBottom: 16,
      animation: "mfPulse 2s infinite",
    }}>
      <div style={{ color: "#FF4D6D", fontSize: 11, letterSpacing: "0.12em", fontWeight: 800, marginBottom: 4 }}>🔴 ACTIVE FIELD TRIGGERS</div>
      <div style={{ color: "#fff", fontSize: 14, fontWeight: 700 }}>
        {unread} unresolved issue{unread === 1 ? "" : "s"} on your propert{unread === 1 ? "y" : "ies"}
      </div>
      <div style={{ color: "#ADBED1", fontSize: 12, marginTop: 6 }}>
        Check your email for the magic-link to view + respond, or scroll down to the project below.
      </div>
      {triggers.slice(0, 3).map((t) => (
        <div key={t.trigger_id} style={{ fontSize: 12, color: "#ADBED1", marginTop: 4 }}>
          → <strong style={{ color: "#fff" }}>{t.address}</strong> · {new Date(t.last_activity_at).toLocaleString()}
        </div>
      ))}
    </div>
  );
};
